import { useState, useEffect } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import AddIntervalModal from './AddIntervalModal';
import AddPartToIntervalModal from './AddPartToIntervalModal';
import { getPartCategoryLabel, getPartUnitLabel } from '../../constants/partCategories';
import {
  IconPlus, IconTrash, IconCheck, IconClock, IconAlertCircle, IconEdit,
} from '@tabler/icons-react';

export default function ServiceScheduleEditor({ makeId, modelId }) {
  const [intervals, setIntervals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddInterval, setShowAddInterval] = useState(false);
  const [editingPartIntervalId, setEditingPartIntervalId] = useState(null);

  useEffect(() => {
    const q = query(
      collection(db, 'makes', makeId, 'models', modelId, 'serviceIntervals'),
      orderBy('triggerHours')
    );
    const unsub = onSnapshot(q, snap => {
      setIntervals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [makeId, modelId]);

  const updateInterval = async (intervalId, changes) => {
    await updateDoc(doc(db, 'makes', makeId, 'models', modelId, 'serviceIntervals', intervalId), {
      ...changes, updatedAt: serverTimestamp(),
    });
  };

  const deleteInterval = async (intervalId, name) => {
    if (!window.confirm('Delete "' + name + '"? This cannot be undone.')) return;
    await deleteDoc(doc(db, 'makes', makeId, 'models', modelId, 'serviceIntervals', intervalId));
  };

  const removePart = async (interval, partId) => {
    const newParts = (interval.partsRequired || []).filter(p => p.partId !== partId);
    await updateInterval(interval.id, { partsRequired: newParts });
  };

  const updatePartQuantity = async (interval, partId, newQty) => {
    const newParts = (interval.partsRequired || []).map(p =>
      p.partId === partId ? { ...p, quantity: parseFloat(newQty) || 0 } : p
    );
    await updateInterval(interval.id, { partsRequired: newParts });
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button size="sm" icon={<IconPlus size={12} stroke={2} />} onClick={() => setShowAddInterval(true)}>
          Add interval
        </Button>
      </div>

      {loading ? (
        <Card><div style={{ padding: 20, color: 'var(--color-text-tertiary)', fontSize: 13 }}>Loading...</div></Card>
      ) : intervals.length === 0 ? (
        <Card>
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <IconClock size={28} stroke={1} color="var(--color-text-tertiary)" style={{ marginBottom: 6 }} />
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
              No service intervals yet.
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
              Click "Add interval" to create the first one.
            </div>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {intervals.map(interval => (
            <IntervalCard
              key={interval.id}
              interval={interval}
              onUpdate={(changes) => updateInterval(interval.id, changes)}
              onDelete={() => deleteInterval(interval.id, interval.name)}
              onAddPart={() => setEditingPartIntervalId(interval.id)}
              onRemovePart={(partId) => removePart(interval, partId)}
              onUpdatePartQty={(partId, qty) => updatePartQuantity(interval, partId, qty)}
            />
          ))}
        </div>
      )}

      {showAddInterval && (
        <AddIntervalModal
          makeId={makeId}
          modelId={modelId}
          existingIntervals={intervals}
          onClose={() => setShowAddInterval(false)}
        />
      )}

      {editingPartIntervalId && (
        <AddPartToIntervalModal
          makeId={makeId}
          modelId={modelId}
          interval={intervals.find(i => i.id === editingPartIntervalId)}
          onClose={() => setEditingPartIntervalId(null)}
        />
      )}
    </>
  );
}

function IntervalCard({ interval, onUpdate, onDelete, onAddPart, onRemovePart, onUpdatePartQty }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(interval.name);
  const [hours, setHours] = useState(interval.triggerHours);
  const [notes, setNotes] = useState(interval.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);

  const parts = interval.partsRequired || [];
  const partsCount = parts.length;

  let statusLabel, statusColor, statusBg;
  if (partsCount === 0) {
    statusLabel = 'Timing only';
    statusColor = 'var(--color-text-tertiary)';
    statusBg = 'var(--color-bg-tertiary)';
  } else if (!interval.partsComplete) {
    statusLabel = 'Draft';
    statusColor = 'var(--color-warning)';
    statusBg = 'var(--color-warning-light)';
  } else {
    statusLabel = 'Complete';
    statusColor = 'var(--color-success)';
    statusBg = 'var(--color-success-light)';
  }

  const saveName = async () => {
    await onUpdate({ name: name.trim(), triggerHours: parseInt(hours) || 0 });
    setEditingName(false);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    await onUpdate({ notes });
    setSavingNotes(false);
  };

  return (
    <Card padding={0}>
      <div style={{ padding: '14px 16px', borderBottom: parts.length > 0 ? '0.5px solid var(--color-border-light)' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          {editingName ? (
            <>
              <input value={name} onChange={e => setName(e.target.value)} style={{ flex: 1 }} placeholder="Interval name" />
              <input type="number" value={hours} onChange={e => setHours(e.target.value)} style={{ width: 80 }} placeholder="Hours" />
              <Button size="sm" variant="secondary" onClick={() => { setName(interval.name); setHours(interval.triggerHours); setEditingName(false); }}>Cancel</Button>
              <Button size="sm" onClick={saveName}>Save</Button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 500, flex: 1 }}>{interval.name}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{interval.triggerHours}h</div>
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: statusBg, color: statusColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {statusLabel}
              </span>
              <div onClick={() => setEditingName(true)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', padding: 4 }}><IconEdit size={14} stroke={1.5} /></div>
              <div onClick={onDelete} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)', padding: 4 }}><IconTrash size={14} stroke={1.5} /></div>
            </>
          )}
        </div>

        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={saveNotes}
          placeholder="Optional notes (oil type, special instructions, etc)"
          rows={2}
          style={{ resize: 'vertical', minHeight: 40, fontSize: 12, marginTop: 6 }}
        />
        {savingNotes && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Saving...</div>}
      </div>

      {parts.length > 0 && (
        <div>
          {parts.map((p, i) => (
            <div key={p.partId} style={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr 140px 90px 60px 30px',
              padding: '10px 16px',
              fontSize: 12,
              gap: 10,
              alignItems: 'center',
              borderBottom: i < parts.length - 1 ? '0.5px solid var(--color-border-light)' : '0.5px solid var(--color-border-light)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)' }}>{p.partNumber}</div>
              <div>{p.partName}</div>
              <div style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{getPartCategoryLabel(p.partCategory)}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="number" step="0.1" value={p.quantity} onChange={e => onUpdatePartQty(p.partId, e.target.value)} style={{ width: 50, padding: '2px 6px', fontSize: 12 }} />
                <span style={{ color: 'var(--color-text-tertiary)' }}>{getPartUnitLabel(p.unit)}</span>
              </div>
              <div></div>
              <div onClick={() => onRemovePart(p.partId)} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>
                <IconTrash size={12} stroke={1.5} />
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--color-bg-secondary)' }}>
        <Button size="sm" variant="secondary" icon={<IconPlus size={12} stroke={2} />} onClick={onAddPart}>
          Add part
        </Button>

        {parts.length > 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!interval.partsComplete}
              onChange={e => onUpdate({ partsComplete: e.target.checked })}
              style={{ width: 14, height: 14, cursor: 'pointer' }}
            />
            <span style={{ color: interval.partsComplete ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
              {interval.partsComplete ? <><IconCheck size={12} stroke={2} style={{ verticalAlign: 'middle' }} /> Parts list complete</> : 'Mark parts list complete'}
            </span>
          </label>
        )}

        {parts.length === 0 && (
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconAlertCircle size={12} stroke={1.5} />
            Ops will see this interval's timing only
          </div>
        )}
      </div>
    </Card>
  );
}
